// Pure helpers for the unified business location model used by the onboarding
// wizard. A business has a single list of `locationEntries`; exactly one is the
// headquarters (the "primary" location). Each entry can come from an address
// search or from a dropped map pin (which may have a custom, address-less
// label). The legacy Firestore fields (hqAddress / hqLocation / locations /
// locationDetails) are derived from this list so downstream consumers and the
// persisted schema keep working unchanged.
//
// These helpers are intentionally pure (no window / Google / Date / random) so
// they can be unit-tested. Ids are derived deterministically from the existing
// list via nextEntryId().
// Self-contained on purpose (only depends on encodeGeohash) so utils.js can
// import it without creating a cycle with placesGeo.js.
import { encodeGeohash } from "../../../services/raffleLocationValidity";

const asArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const toFiniteCoordinate = (value) => {
  // Number(null) is 0 and Number("") is 0, and both are finite — so a bare
  // Number() coercion silently turns "no pin" into "pinned at 0,0" in the Gulf
  // of Guinea. Reject the empty values before coercing, exactly as the server's
  // toFiniteNumber does (functions/businessLocationCascade.js). A real 0 still
  // survives, which is the whole reason this cannot be a falsy check.
  if (value === null || value === undefined || value === "") return null;
  const next = Number(value);
  return Number.isFinite(next) ? next : null;
};

// A business can occupy several distinct spaces at one street address (an
// airport with booths in multiple concourses, a mall, a market, a convention
// centre). These share a `venueName` + `address` but are differentiated by a
// `unitLabel` and an optional `unitType`. The composed `label` stays the single
// display/legacy name so downstream consumers keep working unchanged.
export const UNIT_TYPES = [
  { value: "concourse", label: "Concourse" },
  { value: "gate", label: "Gate" },
  { value: "terminal", label: "Terminal" },
  { value: "unit", label: "Unit" },
  { value: "suite", label: "Suite" },
  { value: "floor", label: "Floor" },
  { value: "stall", label: "Stall" },
  { value: "booth", label: "Booth" },
  { value: "kiosk", label: "Kiosk" },
];

const UNIT_TYPE_VALUES = new Set(UNIT_TYPES.map((t) => t.value));

export const normalizeUnitType = (value) => {
  const next = String(value || "").trim().toLowerCase();
  return UNIT_TYPE_VALUES.has(next) ? next : "";
};

export const unitTypeLabel = (value) => {
  const match = UNIT_TYPES.find((t) => t.value === normalizeUnitType(value));
  return match ? match.label : "";
};

// "Venue — Unit", or whichever side is present. Kept in sync with createEntry /
// updateEntry so the composed label always reflects the venue/unit pieces.
export const composeLocationLabel = ({ venueName = "", unitLabel = "" } = {}) => {
  const venue = String(venueName || "").trim();
  const unit = String(unitLabel || "").trim();
  if (venue && unit) return `${venue} — ${unit}`;
  return venue || unit || "";
};

const buildLocationDetail = ({
  address,
  placeId = "",
  addressComponents = [],
  location = null,
} = {}) => {
  const normalizedAddress = String(address || "").trim();
  if (!normalizedAddress) return null;
  const lat = toFiniteCoordinate(location?.lat);
  const lng = toFiniteCoordinate(location?.lng);
  return {
    address: normalizedAddress,
    placeId: String(placeId || "").trim() || null,
    addressComponents: Array.isArray(addressComponents) ? addressComponents : [],
    ...(lat !== null && lng !== null
      ? { lat, lng, geohash: location?.geohash || encodeGeohash(lat, lng) }
      : {}),
  };
};

// Deterministic, collision-free id from the current list: max numeric suffix +1.
export function nextEntryId(entries) {
  let max = 0;
  asArray(entries).forEach((entry) => {
    const match = /^loc_(\d+)$/.exec(String(entry?.id || ""));
    if (match) max = Math.max(max, Number(match[1]) || 0);
  });
  return `loc_${max + 1}`;
}

export function createEntry(input = {}, id) {
  const lat = toFiniteCoordinate(input.lat);
  const lng = toFiniteCoordinate(input.lng);
  const address = String(input.address || input.label || "").trim();
  const venueName = String(input.venueName || "").trim();
  const unitLabel = String(input.unitLabel || "").trim();
  const unitType = normalizeUnitType(input.unitType);
  // When a venue/unit is supplied, the display label is composed from them so
  // multiple spaces at one address stay distinct; otherwise fall back to the
  // provided label/address (legacy entries, plain address searches).
  const composed = composeLocationLabel({ venueName, unitLabel });
  const label = composed || String(input.label || input.address || "").trim();
  return {
    id: String(id || input.id || "").trim() || "loc_1",
    label,
    // False once a human types a venue name. ABSENT READS AS TRUE, so
    // entries written before this field existed — and everything the
    // migration materialises — heal on the next pin move instead of
    // keeping a label that describes where the pin used to be.
    labelIsAutoDerived: input.labelIsAutoDerived !== false,
    address,
    venueName,
    unitLabel,
    unitType,
    placeId: String(input.placeId || "").trim() || null,
    lat,
    lng,
    geohash:
      lat !== null && lng !== null
        ? String(input.geohash || "").trim() || encodeGeohash(lat, lng)
        : null,
    addressComponents: Array.isArray(input.addressComponents)
      ? input.addressComponents
      : [],
    isHeadquarters: Boolean(input.isHeadquarters),
    source: input.source === "pin" ? "pin" : "search",
  };
}

// Guarantee exactly one HQ. preferredId wins; else keep the current HQ; else
// the first entry becomes HQ.
export function ensureSingleHeadquarters(entries, preferredId = null) {
  const list = asArray(entries);
  if (list.length === 0) return [];
  let hqId = null;
  if (preferredId && list.some((entry) => entry.id === preferredId)) {
    hqId = preferredId;
  } else {
    const current = list.find((entry) => entry.isHeadquarters);
    hqId = current ? current.id : list[0].id;
  }
  return list.map((entry) => ({ ...entry, isHeadquarters: entry.id === hqId }));
}

export function addEntry(entries, entry) {
  const list = asArray(entries);
  const next = [...list, entry];
  return ensureSingleHeadquarters(next, entry?.isHeadquarters ? entry.id : null);
}

export function removeEntry(entries, id) {
  const next = asArray(entries).filter((entry) => entry.id !== id);
  return ensureSingleHeadquarters(next);
}

export function setHeadquarters(entries, id) {
  return ensureSingleHeadquarters(entries, id);
}

// Replace an entry's contents from a full edit (the pin picker's payload) while
// keeping what the row owns: its id, its HQ flag, and its place in the list.
// The patch is authoritative for everything else, so clearing the unit fields
// clears them (and recomposes the display label) rather than merging them back.
export function updateEntry(entries, id, patch = {}) {
  const list = asArray(entries);
  if (!list.some((entry) => entry.id === id)) return list;
  return ensureSingleHeadquarters(
    list.map((entry) =>
      entry.id === id
        ? createEntry({ ...patch, isHeadquarters: entry.isHeadquarters }, entry.id)
        : entry,
    ),
  );
}

const entryToDetail = (entry) =>
  buildLocationDetail({
    address: entry.label || entry.address,
    placeId: entry.placeId,
    addressComponents: entry.addressComponents,
    location:
      entry.lat !== null && entry.lat !== undefined && entry.lng !== null && entry.lng !== undefined
        ? { lat: entry.lat, lng: entry.lng, geohash: entry.geohash }
        : null,
  });

// Derive the legacy hq*/locations/locationDetails fields from the unified list.
export function deriveLegacyLocationFields(entries) {
  const list = asArray(entries);
  const hq = list.find((entry) => entry.isHeadquarters) || list[0] || null;
  return {
    hqAddress: hq ? hq.label || hq.address || "" : "",
    hqPlaceId: hq?.placeId || "",
    hqAddressComponents: Array.isArray(hq?.addressComponents) ? hq.addressComponents : [],
    hqLocation:
      hq && hq.lat !== null && hq.lat !== undefined && hq.lng !== null && hq.lng !== undefined
        ? { lat: hq.lat, lng: hq.lng, geohash: hq.geohash || encodeGeohash(hq.lat, hq.lng) }
        : null,
    locations: list.map((entry) => entry.label || entry.address).filter(Boolean),
    locationDetails: list.map(entryToDetail).filter(Boolean),
  };
}

// Validate/normalize a list loaded from Firestore or the wizard.
export function sanitizeLocationEntries(entries) {
  const list = asArray(entries);
  const cleaned = list
    .map((entry, index) =>
      createEntry(entry, String(entry?.id || "").trim() || `loc_${index + 1}`),
    )
    .filter((entry) => entry.label || entry.address);
  return ensureSingleHeadquarters(cleaned);
}

// Rebuild the unified list from legacy fields (for businesses saved before
// locationEntries existed). HQ becomes the primary entry, then each additional
// location is matched to its detail (by address) for coordinates.
export function reconstructEntriesFromLegacy({
  hqAddress = "",
  hqPlaceId = "",
  hqAddressComponents = [],
  hqLocation = null,
  locations = [],
  locationDetails = [],
} = {}) {
  const detailByAddress = new Map();
  asArray(locationDetails).forEach((detail) => {
    const key = String(detail?.address || "").trim().toLowerCase();
    if (key) detailByAddress.set(key, detail);
  });

  const entries = [];
  let counter = 0;
  const mkId = () => `loc_${(counter += 1)}`;

  const hq = String(hqAddress || "").trim();
  if (hq) {
    const detail = detailByAddress.get(hq.toLowerCase());
    const coords =
      hqLocation && toFiniteCoordinate(hqLocation.lat) !== null
        ? hqLocation
        : detail && toFiniteCoordinate(detail.lat) !== null
          ? detail
          : null;
    entries.push(
      createEntry(
        {
          label: hq,
          address: hq,
          placeId: hqPlaceId || detail?.placeId,
          addressComponents:
            Array.isArray(hqAddressComponents) && hqAddressComponents.length
              ? hqAddressComponents
              : detail?.addressComponents,
          lat: coords?.lat,
          lng: coords?.lng,
          geohash: coords?.geohash,
          source: "search",
          isHeadquarters: true,
        },
        mkId(),
      ),
    );
  }

  asArray(locations).forEach((value) => {
    const address = String(value || "").trim();
    if (!address) return;
    if (hq && address.toLowerCase() === hq.toLowerCase()) return; // already the HQ entry
    const detail = detailByAddress.get(address.toLowerCase());
    entries.push(
      createEntry(
        {
          label: address,
          address,
          placeId: detail?.placeId,
          addressComponents: detail?.addressComponents,
          lat: detail?.lat,
          lng: detail?.lng,
          geohash: detail?.geohash,
          source: "search",
          isHeadquarters: false,
        },
        mkId(),
      ),
    );
  });

  return ensureSingleHeadquarters(entries);
}

/**
 * Whether an entry's label is still auto-derived from its pin.
 *
 * Absent means yes: the field postdates most stored entries, and treating
 * absence as "hand-written" would freeze every legacy label — including the
 * observed loc_1, named "26 Peach Ct" while sitting at 301 Case Blvd.
 */
export function isLabelAutoDerived(entry) {
  return (entry || {}).labelIsAutoDerived !== false;
}
